import type { TemplateDefinition } from "@/api/client";

export type Page = TemplateDefinition["page"];
export type Field = TemplateDefinition["fields"][number];
export type Box = { x: number; y: number; w: number; h: number };
export type Size = { w: number; h: number };

/** CSS px per mm at 100% zoom (96 dpi). */
export const PX_PER_MM = 96 / 25.4;
/** The renderer's pt-to-mm factor (internal/render textHeight). */
export const PT_TO_MM = 0.352778;
/** Zoom factors offered on the canvas; 1 draws the page at its printed size on a 96 dpi screen. */
export const ZOOMS = [0.5, 0.75, 1, 1.5] as const;
export const DEFAULT_ZOOM = 0.75;
/** The renderer draws a text field without size_pt at 14 pt. */
export const DEFAULT_SIZE_PT = 14;

// Portrait width and height, matching internal/render/validate.go pageSizesMM.
const PAGE_SIZES_MM: Record<string, [number, number]> = {
  A3: [297, 420],
  A4: [210, 297],
  A5: [148.5, 210],
  LETTER: [215.9, 279.4],
  LEGAL: [215.9, 355.6],
};

export function pageSizeMM(page: Page): Size | null {
  const size = PAGE_SIZES_MM[String(page.size ?? "").trim().toUpperCase()];
  if (!size) return null;
  switch (String(page.orientation ?? "").trim().toLowerCase()) {
    case "portrait":
    case "p":
      return { w: size[0], h: size[1] };
    case "landscape":
    case "l":
      return { w: size[1], h: size[0] };
    default:
      return null;
  }
}

export function margins(page: Page) {
  const m = page.margins_mm ?? {};
  return { top: m.top ?? 0, right: m.right ?? 0, bottom: m.bottom ?? 0, left: m.left ?? 0 };
}

/** The area inside the margins, which frames the watermark; the full page when unmeasurable. */
export function innerBox(page: Page): Box {
  const size = pageSizeMM(page) ?? { w: 0, h: 0 };
  const m = margins(page);
  return { x: m.left, y: m.top, w: size.w - m.left - m.right, h: size.h - m.top - m.bottom };
}

export const mmToPx = (mm: number, zoom: number) => mm * PX_PER_MM * zoom;
export const pxToMM = (px: number, zoom: number) => px / (PX_PER_MM * zoom);
export const snapMM = (mm: number) => Math.round(mm);
export const clamp = (value: number, lo: number, hi: number) => Math.max(lo, Math.min(value, hi));

export const textHeightMM = (sizePt: number) => Math.max(4, sizePt * PT_TO_MM * 1.2);

/** A text field without w_mm runs to the right margin. */
export function textWidthMM(field: Field, page: Page): number {
  if (field.w_mm !== undefined) return field.w_mm;
  const size = pageSizeMM(page);
  return size ? Math.max(0, size.w - margins(page).right - field.x_mm) : 0;
}

/** Where the renderer draws a field, from the keys it reads for that type. */
export function fieldBox(field: Field, page: Page): Box | null {
  if (!pageSizeMM(page)) return null;
  const { x_mm: x, y_mm: y } = field;
  switch (field.type) {
    case "text":
      return { x, y, w: textWidthMM(field, page), h: textHeightMM(field.size_pt ?? DEFAULT_SIZE_PT) };
    case "qr":
      return { x, y, w: field.size_mm ?? 0, h: field.size_mm ?? 0 };
    default:
      return { x, y, w: field.w_mm ?? 0, h: field.h_mm ?? 0 };
  }
}

/** Scales an image to fit b keeping its aspect ratio, centred, like the renderer's watermark. */
export function fitCentred(b: Box, imgW: number, imgH: number): Box {
  const scale = Math.min(b.w / imgW, b.h / imgH);
  const w = imgW * scale;
  const h = imgH * scale;
  return { x: b.x + (b.w - w) / 2, y: b.y + (b.h - h) / 2, w, h };
}

// The validator wants x < page width, so the last whole mm is one short of it.
const lastMM = (limit: number) => Math.ceil(limit) - 1;

/** A dragged or nudged position: snapped to 1 mm and kept on the page. */
export function clampPosition(field: Field, x: number, y: number, page: Page): { x_mm: number; y_mm: number } {
  const size = pageSizeMM(page);
  if (!size) return { x_mm: snapMM(x), y_mm: snapMM(y) };
  const box = fieldBox({ ...field, x_mm: 0, y_mm: 0 }, page) as Box;
  let maxX = Math.min(Math.floor(size.w - box.w + 1e-9), lastMM(size.w));
  if (field.type === "text" && field.w_mm === undefined) maxX = lastMM(size.w - margins(page).right);
  const maxY = Math.min(Math.floor(size.h - box.h + 1e-9), lastMM(size.h));
  return { x_mm: clamp(snapMM(x), 0, maxX), y_mm: clamp(snapMM(y), 0, maxY) };
}

export type SizePatch = { w_mm?: number; h_mm?: number; size_mm?: number };

/** A resize from a handle: snapped to 1 mm, at least 1 mm, and not past the page edge. */
export function clampSize(field: Field, dims: SizePatch, page: Page): SizePatch {
  const size = pageSizeMM(page);
  const roomW = size ? Math.floor(size.w - field.x_mm + 1e-9) : Infinity;
  const roomH = size ? Math.floor(size.h - field.y_mm + 1e-9) : Infinity;
  const fit = (value: number, room: number) => clamp(snapMM(value), 1, Math.max(1, room));
  const out: SizePatch = {};
  if (dims.w_mm !== undefined) out.w_mm = fit(dims.w_mm, roomW);
  if (dims.h_mm !== undefined) out.h_mm = fit(dims.h_mm, roomH);
  if (dims.size_mm !== undefined) out.size_mm = fit(dims.size_mm, Math.min(roomW, roomH));
  return out;
}
